/**
 * CostAccumulator Component
 *
 * Displays running cost during batch enrichment with budget threshold comparison.
 * Shows formatted cost, percentage of budget, and over-budget warnings.
 *
 * Props:
 * - batchCost (object) - {total: number, byJob: object} - Accumulated cost data
 * - budgetThreshold (number) - User's configured budget threshold
 * - processing (boolean) - Whether batch is actively processing
 *
 * Only renders when processing=true AND batchCost.total > 0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const CostAccumulator = ({ batchCost, budgetThreshold, processing }) => {
  // Only render when processing and cost > 0
  if (!processing || !batchCost || batchCost.total <= 0) {
    return null;
  }

  // Format cost with 2-4 decimal places based on value
  const formatCost = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: value < 0.01 ? 4 : 2
    }).format(value);
  };

  // Calculate percentage of budget if threshold is configured
  const percentOfBudget = budgetThreshold && budgetThreshold > 0
    ? (batchCost.total / budgetThreshold) * 100
    : null;

  // Determine if over budget
  const isOverBudget = percentOfBudget && percentOfBudget > 100;

  // Determine badge variant based on budget status
  const badgeVariant = isOverBudget ? 'danger' : 'secondary';
  const costClassName = isOverBudget ? 'text-danger' : 'text-success';

  return (
    <Card bg="light" border="secondary" className="mb-3">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon
              icon={faDollarSign}
              className={costClassName}
              style={{ fontSize: '1rem' }}
            />
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
              Running Cost:
            </span>
            <span
              className={`fw-bold ${costClassName}`}
              style={{ fontSize: '1.1rem' }}
            >
              {formatCost(batchCost.total)}
            </span>

            {percentOfBudget !== null && (
              <Badge bg={badgeVariant} className="ms-2">
                {percentOfBudget.toFixed(0)}% of budget
              </Badge>
            )}
          </div>

          {isOverBudget && (
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-danger"
              />
              <span className="text-danger" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                Over budget
              </span>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

CostAccumulator.propTypes = {
  batchCost: PropTypes.shape({
    total: PropTypes.number,
    byJob: PropTypes.object
  }),
  budgetThreshold: PropTypes.number,
  processing: PropTypes.bool
};

CostAccumulator.defaultProps = {
  batchCost: { total: 0, byJob: {} },
  budgetThreshold: null,
  processing: false
};

export default CostAccumulator;
